import { forwardRef, useReducer } from 'react';

import {
	ACTIONS,
	IHueTextInputProps,
	IInputAction,
	IInputActionStates,
} from './HueTextInput.model';
import {
	StyledContainer,
	StyledHint,
	StyledInput,
} from './HueTextInput.styled';

const reducer = (
	state: IInputActionStates,
	action: IInputAction,
): IInputActionStates => {
	const { type, value } = action;
	switch (type) {
		case ACTIONS.IS_HOVERED:
		case ACTIONS.IS_FOCUSED: {
			const newState = { ...state, [type]: value };
			const isActive = newState.focused || newState.hovered;
			return { ...newState, active: isActive };
		}
		default: {
			return state;
		}
	}
};

const HueTextInput = forwardRef<HTMLInputElement, IHueTextInputProps>(
	(props, ref) => {
		const [state, dispatch] = useReducer(reducer, {
			hovered: false,
			focused: false,
			active: false,
		});

		const {
			hint,
			onMouseEnter,
			onMouseLeave,
			onFocus,
			onBlur,
			onChange,
			...rest
		} = props;

		const handleMouseEnter = (
			event: React.MouseEvent<HTMLInputElement, MouseEvent>,
		) => {
			dispatch({ type: ACTIONS.IS_HOVERED, value: true });
			if (onMouseEnter) onMouseEnter(event);
			event.preventDefault();
		};

		const handleMouseLeave = (
			event: React.MouseEvent<HTMLInputElement, MouseEvent>,
		) => {
			dispatch({ type: ACTIONS.IS_HOVERED, value: false });
			if (onMouseLeave) onMouseLeave(event);
			event.preventDefault();
		};

		const handleFocus = (
			event: React.FocusEvent<HTMLInputElement, Element>,
		) => {
			dispatch({ type: ACTIONS.IS_FOCUSED, value: true });
			if (onFocus) onFocus(event);
			event.preventDefault();
		};

		const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
			dispatch({ type: ACTIONS.IS_FOCUSED, value: false });
			if (onBlur) onBlur(event);
			event.preventDefault();
		};

		const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			if (onChange) onChange(event);
			event.preventDefault();
		};

		return (
			<StyledContainer>
				<StyledHint data-hint={hint} $active={state.active}>
					<StyledInput
						ref={ref}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onChange={handleChange}
						$active={state.active}
						{...rest}
					/>
				</StyledHint>
			</StyledContainer>
		);
	},
);

export { HueTextInput };
